/* eslint-disable @typescript-eslint/no-unused-vars, @typescript-eslint/explicit-module-boundary-types, no-underscore-dangle, @typescript-eslint/naming-convention*/
import { TemplatePortal } from '@angular/cdk/portal';
import { NgClass, NgFor, NgIf } from '@angular/common';
import {
	AfterViewInit,
	Component,
	EventEmitter,
	HostListener,
	Input,
	NgZone,
	OnChanges,
	OnDestroy,
	Output,
	Renderer2,
	SimpleChanges,
	TemplateRef,
	ViewChild,
	ViewContainerRef,
	ViewEncapsulation,
} from '@angular/core';
import { AbstractControl, FormsModule, ReactiveFormsModule, UntypedFormGroup } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatToolbarModule } from '@angular/material/toolbar';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { FormlyFieldConfig, FormlyFormOptions, FormlyModule } from '@ngx-formly/core';
import { cloneDeep, get } from 'lodash';
import { BehaviorSubject, Subject, combineLatest } from 'rxjs';
import { filter, first, take, takeUntil } from 'rxjs/operators';
import { FORMLY_FIELD_TYPES, FORMLY_FIELD_WRAPPERS } from '@app/constants';
import { DialogService, FlyoutService, LoadingService, NavigationService } from '@app/core/services';
import { BaseFlyoutComponent } from '@app/shared/components/base-flyout.component';
import { FormComponentActionButtons } from './models/form-component-action-buttons.model';
import { NgForKeyDirective } from '../../directives/ng-for-track-by-id.directive';

export const FormMode = {
	View: 'view',
	Edit: 'edit',
};

export const DefaultFormState = {
	mode: FormMode.View,
	flyout: true,
	readOnly: true,
};

@Component({
	selector: 'sl-form',
	styleUrls: ['./form.component.less'],
	templateUrl: './form.component.html',
	encapsulation: ViewEncapsulation.None,
	standalone: true,
	imports: [
		NgIf,
		FormsModule,
		ReactiveFormsModule,
		FormlyModule,
		MatIconModule,
		MatToolbarModule,
		MatButtonModule,
		NgFor,
		NgForKeyDirective,
		NgClass,
	],
})
export class FormComponent extends BaseFlyoutComponent implements OnChanges, OnDestroy, AfterViewInit {
	@ViewChild('flyoutTemplate') public flyoutTemplate: TemplateRef<any>;

	@Output()
	submitted = new EventEmitter<any>();

	@Input()
	readOnly = false;

	// eslint-disable-next-line @angular-eslint/no-input-rename
	@Input('form')
	editForm = new UntypedFormGroup({});

	@Input()
	model = {};

	@Input()
	entityId?: any;

	@Input()
	fields: FormlyFieldConfig[] = [];

	@Input()
	options = {
		formState: {
			...DefaultFormState,
		},
	} as FormlyFormOptions;

	@Input() editOnly = false;

	@Input() flyoutTitle: string;

	@Input() disableUnsavedChanges = false;

	@Input() hideSaveButton = false;

	@Input() actionButtons: FormComponentActionButtons[] = [];

	@Input() keepUserFormatting = true;

	public _fields: FormlyFieldConfig[] = [];
	public _options: FormlyFormOptions = {
		formState: {
			...DefaultFormState,
		},
	} as FormlyFormOptions;

	public openedSections: Set<string> = new Set();

	public viewForm = new UntypedFormGroup({});

	public calculatedFlyoutTitle: string;

	public confirmButtonText = 'Save';

	public cancelButtonText = 'Cancel';

	private query: Params;

	private readonly fields$ = new BehaviorSubject<FormlyFieldConfig[]>(undefined);

	private readonly destroy$ = new Subject();

	constructor(
		protected readonly navigationService: NavigationService,
		protected readonly dialog: MatDialog,
		protected readonly dialogService: DialogService,
		protected readonly flyoutService: FlyoutService,
		private readonly viewContainerRef: ViewContainerRef,
		private readonly route: ActivatedRoute,
		private readonly router: Router,
		private readonly loadingService: LoadingService,
		private readonly ngZone: NgZone,
		private readonly renderer: Renderer2
	) {
		super(flyoutService, dialogService);
	}

	@HostListener('window:beforeunload')
	canDeactivate = (): boolean => this.disableUnsavedChanges || !this.editForm?.dirty;

	get isViewMode(): boolean {
		return this._options.formState?.mode === FormMode.View;
	}

	get isEditMode(): boolean {
		return this._options.formState?.mode === FormMode.Edit;
	}

	get isReadOnly(): boolean {
		return this._options.formState?.readOnly;
	}

	onSubmit(model: any): void {
		this.submitted.emit(model);
	}

	ngAfterViewInit(): void {
		combineLatest([this.route.queryParams, this.fields$])
			.pipe(
				takeUntil(this.destroy$),
				filter(([, fields]) => fields && fields.length > 0)
			)
			.subscribe(([params]) => {
				this.query = params;
				if (params.sections) {
					this.openedSections.clear();
					[params.sections].flat().forEach((section) => this.openedSections.add(section));
				}
				if (Number(params.openFlyout)) {
					// wait until all loading is complete before opening flyout
					this.loadingService.loading$
						.pipe(
							first((loading) => !loading),
							takeUntil(this.destroy$)
						)
						.subscribe(() => {
							this.edit();
						});
				}
			});

		this.recalculateFlyoutTitle();
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes.fields) {
			this.fields$.next(changes.fields.currentValue);
			const fields = changes.fields.currentValue;
			this._fields = this.applyTransforms(fields, this.options?.formState);
		}

		if (changes.options) {
			this._options = changes.options.currentValue;
			// form initialized as Edit mode
			if (changes.options.isFirstChange() && this.isEditMode) {
				this.viewForm = this.editForm;
			}
		}

		this.recalculateFlyoutTitle();
	}

	ngOnDestroy(): void {
		this.destroy$.next(undefined);
		this.destroy$.complete();
	}

	public edit(section?: string): void {
		if (this.isReadOnly) {
			return;
		}

		// ensures that @Input model is updated before `edit` continues
		this.ngZone.onStable.pipe(first()).subscribe(() => {
			if (section) {
				this.openedSections.add(section);
			}

			const nextMode = this.isViewMode || this.editOnly ? FormMode.Edit : FormMode.View;
			const options = { ...this.options, formState: { ...this.options?.formState, mode: nextMode } };
			const fields = this.applyTransforms(this.fields, options.formState);
			if (options.formState?.flyout) {
				this.flyoutService.close();
				const context = {
					form: this.editForm,
					model: cloneDeep(this.model),
					fields,
					options,
					canDeactivate: this.canDeactivate,
					close: this.close,
				};

				this.recalculateFlyoutTitle();
				const portal = new TemplatePortal(this.flyoutTemplate, this.viewContainerRef, context);
				this.flyoutService.open(portal);
			} else {
				this._fields = fields;
				this._options = options;
				this.viewForm.enable();
			}
		});
	}

	public closeFlyout(): void {
		this.close()
			.pipe(take(1))
			.subscribe((closed) => {
				if (closed) {
					this.openedSections.clear();
					this.editForm.reset(this.model);
					if (this.query.cancelRedirect) {
						const redirect = [this.query.cancelRedirect].flat();
						this.router.navigate(redirect);
					}
					this._options = this.options;
				}
			});
	}

	public setFocus(key: string, delayValue = 100, selectionRange: number[] = null): void {
		this.ngZone.runOutsideAngular(() => {
			setTimeout(() => {
				const control = this.editForm.get(key);
				if (control === null) {
					return;
				}

				const typesWithElementRefs = [
					FORMLY_FIELD_TYPES.autocomplete,
					FORMLY_FIELD_TYPES.multiuseSearchInput,
					FORMLY_FIELD_TYPES.maskedInput,
					FORMLY_FIELD_TYPES.inputPositiveIntegers,
				];

				const selector = typesWithElementRefs.includes(get(control, '_fields[0].type', null))
					? `#${get(control, '_fields[0]._elementRefs[0].nativeElement.id', '')}`
					: `#${get(control, '_fields[0].id', '')}`;
				this.renderer.selectRootElement(selector).focus();
				if (selectionRange && selectionRange.length === 2) {
					this.renderer.selectRootElement(selector).setSelectionRange(selectionRange[0], selectionRange[1]);
				}
			}, delayValue);
		});
	}

	public setFocusByControl(control: AbstractControl, delayValue = 100, selectionRange: number[] = null): void {
		this.ngZone.runOutsideAngular(() => {
			setTimeout(() => {
				if (control === null) {
					return;
				}

				const typesWithElementRefs = [
					FORMLY_FIELD_TYPES.autocomplete,
					FORMLY_FIELD_TYPES.multiuseSearchInput,
					FORMLY_FIELD_TYPES.maskedInput,
					FORMLY_FIELD_TYPES.inputPositiveIntegers,
				];

				const selector = typesWithElementRefs.includes(get(control, '_fields[0].type', null))
					? `#${get(control, '_fields[0]._elementRefs[0].nativeElement.id', '')}`
					: `#${get(control, '_fields[0].id', '')}`;

				this.renderer.selectRootElement(selector).focus();
				if (selectionRange && selectionRange.length === 2) {
					this.renderer.selectRootElement(selector).setSelectionRange(selectionRange[0], selectionRange[1]);
				}
			}, delayValue);
		});
	}

	public updateContextModel(patch?: any): void {
		this.getPortal().context.model = patch ? { ...this.model, ...patch } : this.model;
	}

	public getContextModel(): any {
		return this.getPortal()?.context.model;
	}

	/**
	 * TODO? Use formly extensions instead?
	 */
	private applyTransforms(_fields: FormlyFieldConfig[], formState?: any): FormlyFieldConfig[] {
		const fields = cloneDeep(_fields);

		if (formState) {
			if (formState.mode === FormMode.Edit) {
				this.applyEdit(fields);
				if (formState.flyout) {
					this.applyFlyout(fields);
					this.applyOpenSections(fields);
				}
			}

			if (!formState.mode || formState.mode === FormMode.View) {
				this.applyViewMode(fields);
				if (formState.readOnly) {
					this.applyReadOnly(fields);
				}
			}
		}

		return fields;
	}

	private recalculateFlyoutTitle() {
		const modelId = (this.model as any)?.Id ?? 0;
		this.calculatedFlyoutTitle = this.flyoutTitle || (this.entityId > 0 || modelId > 0 ? 'Edit' : 'Add');
	}

	private applyOpenSections(fields: FormlyFieldConfig[]) {
		fields.forEach((field) => {
			if (field.props) {
				field.props.sectionsOpened = [...this.openedSections];
			}
		});
	}

	private readonly applyViewMode = (fields: FormlyFieldConfig[]) => {
		fields.forEach((field) => {
			if (Array.isArray(field.fieldGroup) && field.fieldGroup.length > 0) {
				field.fieldGroup.forEach((groupField) => {
					// NOTE: The form defaults to view/readonly mode, so this code can run before Formly
					// initializes the field config. If you are setting templateOptions instead of props,
					// the props object won't be initialized yet and these properties won't be applied to
					// the field, which will leave the field editable.
					// We should check for templateOptions if props is not defined, but that could be a
					// breaking change, so for now this will remain as-is.
					if (groupField.props) {
						Object.assign(groupField.props, {
							readonly: true,
							readOnly: true,
							appearance: groupField.props.appearance || 'none',
							floatLabel: groupField.props.floatLabel || 'always',
						});
						if (groupField.type === 'datepicker') {
							groupField.props.datepickerOptions = Object.assign({}, groupField.props.datepickerOptions, {
								datepickerTogglePosition: 'none', // hide calendar icon toggle
							});
						}

						if (groupField.type === 'select') {
							groupField.props.disabled = true;
						}
					}
				});
			}
		});
	};

	private readonly applyReadOnly = (fields: FormlyFieldConfig[]) => {
		fields.forEach((field) => {
			if (Array.isArray(field.fieldGroup) && field.fieldGroup.length > 0) {
				field.fieldGroup.forEach((groupField) => {
					// See note in applyViewMode about props vs templateOptions, which also impacts this function.
					if (groupField.props) {
						groupField.props.readonly = true;
					}
				});
			}
		});
	};

	private readonly applyFlyout = (fields: FormlyFieldConfig[]) => {
		if (this.editOnly && this.keepUserFormatting) {
			//Don't change anything from the way the dev configured it if editOnly
			return;
		}
		fields.forEach((field) => {
			if (Array.isArray(field.fieldGroup) && field.fieldGroup.length > 0) {
				field.wrappers = fields.length === 1 ? [FORMLY_FIELD_WRAPPERS.sectionSimple] : [FORMLY_FIELD_WRAPPERS.sectionAccordion];
				if (field.fieldGroupClassName?.indexOf('row') >= 0) {
					field.fieldGroup.forEach((groupField) => {
						groupField.className = 'col-xs-12';
					});
				}
			}
		});
	};

	private readonly applyEdit = (fields: FormlyFieldConfig[]) => {
		fields.forEach((field) => {
			if (Array.isArray(field.fieldGroup) && field.fieldGroup.length > 0) {
				field.fieldGroup.forEach((groupField) => {
					if (groupField.props) {
						Object.assign(groupField.props, {
							appearance: groupField.props.appearance || 'outline',
							color: groupField.props.color || 'primary',
						});
					}
				});
			}
		});
	};
}
